import { useState } from 'react'
import { Shield, ShieldOff, SkipForward } from 'lucide-react'

interface WelcomeScreenProps {
  onEnable: () => void
  onSkip: () => void
}

export function WelcomeScreen({ onEnable, onSkip }: WelcomeScreenProps) {
  return (
    <div className="app-lock-welcome">
      <div className="app-lock-welcome__card">
        <Shield size={48} className="app-lock-welcome__icon" />
        <h1 className="app-lock-welcome__title">Protect Your Data</h1>
        <p className="app-lock-welcome__desc">
          Add a lock screen to prevent unauthorized access to your calculator settings and history.
        </p>
        <div className="app-lock-welcome__actions">
          <button
            type="button"
            className="app-lock-welcome__enable-btn"
            onClick={onEnable}
          >
            <Shield size={18} />
            Enable Lock Screen
          </button>
          <button
            type="button"
            className="app-lock-welcome__skip-btn"
            onClick={onSkip}
          >
            <SkipForward size={16} />
            Skip for now
          </button>
        </div>
        <p className="app-lock-welcome__hint">
          You can enable this later from the header menu.
        </p>
      </div>
    </div>
  )
}

interface SetupScreenProps {
  onSubmit: (password: string) => Promise<void>
  onSkip: () => void
}

export function SetupScreen({ onSubmit, onSkip }: SetupScreenProps) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password.length < 4) {
      setError('Password must be at least 4 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    setSubmitting(true)
    try {
      await onSubmit(password)
    } catch {
      setError('Failed to set password. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="app-lock-setup">
      <div className="app-lock-setup__card">
        <Shield size={36} className="app-lock-setup__icon" />
        <h2 className="app-lock-setup__title">Create Password</h2>
        <form onSubmit={handleSubmit} className="app-lock-setup__form">
          <div className="app-lock-setup__field">
            <label htmlFor="lock-password" className="app-lock-setup__label">Password</label>
            <input
              id="lock-password"
              type="password"
              className="app-lock-setup__input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 4 characters"
              autoFocus
              autoComplete="new-password"
            />
          </div>
          <div className="app-lock-setup__field">
            <label htmlFor="lock-confirm" className="app-lock-setup__label">Confirm Password</label>
            <input
              id="lock-confirm"
              type="password"
              className="app-lock-setup__input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter password"
              autoComplete="new-password"
            />
          </div>
          {error && <p className="app-lock-setup__error" role="alert">{error}</p>}
          <div className="app-lock-setup__actions">
            <button
              type="submit"
              className="app-lock-setup__submit-btn"
              disabled={submitting || !password || !confirm}
            >
              {submitting ? 'Setting up...' : 'Set Password'}
            </button>
            <button
              type="button"
              className="app-lock-setup__skip-btn"
              onClick={onSkip}
            >
              Cancel
            </button>
          </div>
        </form>
        <p className="app-lock-setup__warning">
          If you forget your password, there is no recovery. Your data will remain accessible but locked.
        </p>
      </div>
    </div>
  )
}

interface LockScreenProps {
  onUnlock: (password: string) => Promise<boolean>
}

export function LockScreen({ onUnlock }: LockScreenProps) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [attempting, setAttempting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setAttempting(true)
    try {
      const success = await onUnlock(password)
      if (!success) {
        setError('Wrong password. Try again.')
        setPassword('')
      }
    } catch {
      setError('Unlock failed. Try again.')
    } finally {
      setAttempting(false)
    }
  }

  return (
    <div className="app-lock-screen">
      <div className="app-lock-screen__card">
        <ShieldOff size={48} className="app-lock-screen__icon" />
        <h2 className="app-lock-screen__title">App Locked</h2>
        <p className="app-lock-screen__desc">Enter your password to continue.</p>
        <form onSubmit={handleSubmit} className="app-lock-screen__form">
          <div className="app-lock-screen__field">
            <label htmlFor="lock-unlock-password" className="sr-only">Password</label>
            <input
              id="lock-unlock-password"
              type="password"
              className="app-lock-screen__input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              autoComplete="current-password"
            />
          </div>
          {error && <p className="app-lock-screen__error" role="alert">{error}</p>}
          <button
            type="submit"
            className="app-lock-screen__unlock-btn"
            disabled={attempting || !password}
          >
            {attempting ? 'Unlocking...' : 'Unlock'}
          </button>
        </form>
      </div>
    </div>
  )
}
